import type { EffectiveRole, RolePermissionFlags, role } from 'auth-types';
import type { ActiveView } from '@/lib/slices/login-slice';

import EffectiveRoleService from './effective-role-service';

type PermissionKey = keyof RolePermissionFlags;

const ADMIN_HOME_VIEW_PERMISSIONS: PermissionKey[] = [
  'perm_enterprise_exclusive',
  'perm_subdomain_user_manage_students',
  'perm_subdomain_user_manage_all_non_enterprise',
  'perm_role_management',
  'perm_branding_management',
  'perm_sso_saml',
  'perm_audit_logs',
  'perm_billing',
  'perm_view_reports',
  'perm_export_reports',
  'perm_ai_usage_data',
];

const ADMIN_DEFAULT_PRIORITY_PERMISSIONS: PermissionKey[] = [
  'perm_enterprise_exclusive',
  'perm_subdomain_user_manage_students',
  'perm_subdomain_user_manage_all_non_enterprise',
  'perm_role_management',
  'perm_branding_management',
  'perm_sso_saml',
  'perm_audit_logs',
  'perm_billing',
  'perm_export_reports',
  'perm_ai_usage_data',
];

const INSTITUTE_OVERVIEW_DATA_PERMISSIONS: PermissionKey[] = [
  'perm_enterprise_exclusive',
  'perm_view_reports',
  'perm_export_reports',
  'perm_ai_usage_data',
];

const INSTRUCTOR_HOME_VIEW_PERMISSIONS: PermissionKey[] = [
  'perm_enterprise_exclusive',
  'perm_course_view',
  'perm_course_manage',
  'perm_group_view',
  'perm_group_manage',
  'perm_group_analytics',
  'perm_group_grade_edit',
];

const hasAnyPermission = (
  effectiveRole: EffectiveRole | null | undefined,
  permissions: PermissionKey[],
): boolean =>
  permissions.some((permission) => EffectiveRoleService.hasPermission(effectiveRole, permission));

const isAdminPortalRole = (
  effectiveRole: EffectiveRole | null | undefined,
  fallbackRole: role,
): boolean => EffectiveRoleService.resolveRoleType(effectiveRole, fallbackRole) === 'admin';

const isStudentPortalRole = (
  effectiveRole: EffectiveRole | null | undefined,
  fallbackRole: role,
): boolean => EffectiveRoleService.resolveRoleType(effectiveRole, fallbackRole) === 'student';

const HomeViewAccessService = {
  canUseAdminView(effectiveRole: EffectiveRole | null | undefined): boolean {
    return hasAnyPermission(effectiveRole, ADMIN_HOME_VIEW_PERMISSIONS);
  },

  canUseInstructorView(effectiveRole: EffectiveRole | null | undefined): boolean {
    return hasAnyPermission(effectiveRole, INSTRUCTOR_HOME_VIEW_PERMISSIONS);
  },

  canViewInstituteOverviewData(effectiveRole: EffectiveRole | null | undefined): boolean {
    return hasAnyPermission(effectiveRole, INSTITUTE_OVERVIEW_DATA_PERMISSIONS);
  },

  canManageCalendarEvents(effectiveRole: EffectiveRole | null | undefined): boolean {
    return (
      EffectiveRoleService.hasPermission(effectiveRole, 'perm_enterprise_exclusive') ||
      EffectiveRoleService.hasPermission(effectiveRole, 'perm_subdomain_user_manage_students') ||
      EffectiveRoleService.hasPermission(
        effectiveRole,
        'perm_subdomain_user_manage_all_non_enterprise',
      )
    );
  },

  canManageStudents(effectiveRole: EffectiveRole | null | undefined): boolean {
    return EffectiveRoleService.hasPermission(effectiveRole, 'perm_subdomain_user_manage_students');
  },

  canManageInstructors(effectiveRole: EffectiveRole | null | undefined): boolean {
    return EffectiveRoleService.hasPermission(
      effectiveRole,
      'perm_subdomain_user_manage_all_non_enterprise',
    );
  },

  canAccessCourses(effectiveRole: EffectiveRole | null | undefined): boolean {
    return EffectiveRoleService.hasPermission(effectiveRole, 'perm_course_view');
  },

  getAvailableViews(
    effectiveRole: EffectiveRole | null | undefined,
    fallbackRole: role,
  ): ActiveView[] {
    if (isStudentPortalRole(effectiveRole, fallbackRole)) {
      return ['student'];
    }

    if (!isAdminPortalRole(effectiveRole, fallbackRole)) {
      return [];
    }

    const availableViews: ActiveView[] = [];
    if (this.canUseAdminView(effectiveRole)) {
      availableViews.push('admin');
    }
    if (this.canUseInstructorView(effectiveRole)) {
      availableViews.push('instructor');
    }

    if (availableViews.length === 0 && fallbackRole) {
      availableViews.push('instructor');
    }

    return availableViews;
  },

  resolveDefaultView(
    effectiveRole: EffectiveRole | null | undefined,
    fallbackRole: role,
  ): ActiveView | null {
    const availableViews = this.getAvailableViews(effectiveRole, fallbackRole);
    if (availableViews.length === 0) {
      return null;
    }
    if (availableViews.length === 1) {
      return availableViews[0];
    }

    if (hasAnyPermission(effectiveRole, ADMIN_DEFAULT_PRIORITY_PERMISSIONS)) {
      return 'admin';
    }

    return availableViews.includes('instructor') ? 'instructor' : availableViews[0];
  },
};

export default HomeViewAccessService;
